/**
 * mediaLibraryState — o estado da biblioteca de imagens como um redutor PURO (sem React, sem rede).
 *
 * Toda regra de "o que acontece quando" mora aqui e é testada em `__tests__/mediaLibraryState.test.js`: escolher na
 * ordem do toque, respeitar o limite da galeria, foto enviada entrar escolhida, foto repetida reaproveitar a existente,
 * apagar foto em uso pedir a segunda confirmação (M37). O `useMediaLibrary` só faz efeitos e despacha ações; o
 * `mediaLibraryVmBuilder` traduz este estado no ViewModel.
 */
import { MediaOrigin } from '../../catalogs/pageCatalog.generated.js';
import { MAX_FOTOS_POR_GALERIA } from './mediaLibraryVmBuilder.js';

export const estadoInicial = {
  mode: 'gallery',
  vagas: MAX_FOTOS_POR_GALERIA,
  jaNaGaleria: [],
  logoAtual: null,
  carregando: false,
  carregandoMais: false,
  erro: null,
  itens: [],
  provisorios: [],      // fotos que acabaram de subir e a Mídia ainda não lista (ver itemDoEnvio)
  proximo: null,
  cota: { usadas: 0, limite: null },
  envios: [],
  selecao: [],
  apagando: [],
  descricao: { aberta: false, itemId: null, valor: '', salvando: false, erro: null },
  apagar: { aberto: false, itemId: null, emUso: null, apagando: false, erro: null },
  aviso: null,
};

const semRepetir = (lista) => {
  const vistos = new Set();
  return lista.filter((i) => (vistos.has(i.assetId) ? false : (vistos.add(i.assetId), true)));
};

/**
 * Lista do servidor + fotos recém-enviadas que ele ainda não devolve (entram no topo, onde ficam as do dono).
 * Quando o servidor passa a listar uma provisória, a do servidor vence (traz `usadoEm`, descrição etc.).
 */
export function mesclar(doServidor, provisorios) {
  const ids = new Set(doServidor.map((i) => i.assetId));
  return semRepetir([...provisorios.filter((p) => !ids.has(p.assetId)), ...doServidor]);
}

/** Pode entrar na escolha da galeria? (não está na galeria e ainda cabe) */
function cabeNaGaleria(s, id) {
  return !s.jaNaGaleria.includes(id) && s.selecao.length < s.vagas;
}

function escolher(s, id) {
  if (s.mode === 'logo') return { ...s, selecao: [id] };
  if (s.selecao.includes(id) || !cabeNaGaleria(s, id)) return s;
  return { ...s, selecao: [...s.selecao, id] };
}

const mudarEnvio = (s, id, mudanca) => ({ ...s, envios: s.envios.map((e) => (e.id === id ? { ...e, ...mudanca } : e)) });

export function reduzir(s, a) {
  switch (a.type) {
    case 'ABRIR':
      return {
        ...estadoInicial,
        mode: a.mode === 'logo' ? 'logo' : 'gallery',
        vagas: Math.max(0, a.vagas ?? MAX_FOTOS_POR_GALERIA),
        jaNaGaleria: a.jaNaGaleria ?? [],
        logoAtual: a.logoAtual ?? null,
        carregando: true,
      };
    case 'CARREGANDO_MAIS':
      return { ...s, carregandoMais: true };
    case 'CARREGOU': {
      const p = a.pagina ?? {};
      const novos = p.itens ?? [];
      const itens = a.mais ? semRepetir([...s.itens, ...novos]) : mesclar(novos, s.provisorios);
      const listados = new Set(novos.map((i) => i.assetId));
      return {
        ...s,
        carregando: false,
        carregandoMais: false,
        erro: null,
        itens,
        provisorios: a.mais ? s.provisorios : s.provisorios.filter((x) => !listados.has(x.assetId)),
        proximo: p.proximo ?? null,
        cota: p.cota ?? s.cota,
      };
    }
    case 'FALHOU_CARREGAR':
      return { ...s, carregando: false, carregandoMais: false, erro: a.mais ? s.erro : { mensagem: a.mensagem },
        aviso: a.mais ? { tone: 'error', message: a.mensagem } : s.aviso };

    case 'ENVIO_NA_FILA':
      return { ...s, envios: [{ id: a.id, nome: a.nome, previewUrl: a.previewUrl ?? null, etapa: 'waiting', progresso: 0, erro: null }, ...s.envios] };
    case 'ENVIO_ETAPA':
      return mudarEnvio(s, a.id, { etapa: a.etapa, progresso: a.progresso ?? 0 });
    case 'ENVIO_FALHOU':
      return mudarEnvio(s, a.id, { etapa: 'error', erro: { codigo: a.codigo, mensagem: a.mensagem } });
    case 'ENVIO_REPETIR':
      return mudarEnvio(s, a.id, { etapa: 'waiting', progresso: 0, erro: null });
    case 'ENVIO_DISPENSAR':
      return { ...s, envios: s.envios.filter((e) => e.id !== a.id) };
    case 'ENVIO_PRONTO': {
      // `duplicado`: o dono já tinha a foto — nada subiu, a existente é a que vale (M13).
      const item = a.item;
      let n = mudarEnvio(s, a.id, { etapa: a.duplicado ? 'duplicate' : 'done', progresso: 100 });
      const jaListado = n.itens.some((i) => i.assetId === item.assetId);
      if (!jaListado) {
        n = { ...n, itens: mesclar(n.itens, [item]) };
        if (!a.duplicado) n = { ...n, provisorios: [...n.provisorios, item] };
      }
      if (!a.duplicado) n = { ...n, cota: { ...n.cota, usadas: (n.cota.usadas || 0) + 1 } };
      return escolher(n, item.assetId);   // quem envia quer usar
    }

    case 'ALTERNAR': {
      if (s.mode === 'logo') return { ...s, selecao: s.selecao[0] === a.id ? [] : [a.id] };
      if (s.selecao.includes(a.id)) return { ...s, selecao: s.selecao.filter((x) => x !== a.id) };
      return escolher(s, a.id);
    }

    case 'DESCRICAO_ABRIR': {
      const alvo = s.itens.find((i) => i.assetId === a.id);
      if (!alvo || alvo.origem !== MediaOrigin.Owner) return s;   // exemplo não se descreve
      return { ...s, descricao: { aberta: true, itemId: a.id, valor: alvo.alt ?? '', salvando: false, erro: null } };
    }
    case 'DESCRICAO_MUDAR':
      return { ...s, descricao: { ...s.descricao, valor: a.valor, erro: null } };
    case 'DESCRICAO_SALVANDO':
      return { ...s, descricao: { ...s.descricao, salvando: true, erro: null } };
    case 'DESCRICAO_SALVA':
      return {
        ...s,
        itens: s.itens.map((i) => (i.assetId === a.id ? { ...i, alt: a.alt || null } : i)),
        descricao: { ...estadoInicial.descricao },
        aviso: { tone: 'success', message: 'Descrição salva.' },
      };
    case 'DESCRICAO_FALHOU':
      return { ...s, descricao: { ...s.descricao, salvando: false, erro: a.mensagem } };
    case 'DESCRICAO_FECHAR':
      return { ...s, descricao: { ...estadoInicial.descricao } };

    case 'APAGAR_PERGUNTAR': {
      const alvo = s.itens.find((i) => i.assetId === a.id);
      if (!alvo || alvo.origem !== MediaOrigin.Owner) return s;   // exemplo não se apaga
      return { ...s, apagar: { aberto: true, itemId: a.id, emUso: Number(alvo.usadoEm) || 0, apagando: false, erro: null } };
    }
    case 'APAGAR_ENVIANDO':
      return { ...s, apagar: { ...s.apagar, apagando: true, erro: null }, apagando: [...s.apagando, s.apagar.itemId] };
    case 'APAGAR_EM_USO':
      // A lista estava velha: o servidor diz que a foto está em uso. Mostra a confirmação de peso (M37) e espera.
      return { ...s, apagar: { ...s.apagar, apagando: false, emUso: a.usadoEm }, apagando: s.apagando.filter((x) => x !== s.apagar.itemId) };
    case 'APAGADA':
      return {
        ...s,
        itens: s.itens.filter((i) => i.assetId !== a.id),
        provisorios: s.provisorios.filter((i) => i.assetId !== a.id),
        selecao: s.selecao.filter((x) => x !== a.id),
        apagando: s.apagando.filter((x) => x !== a.id),
        cota: { ...s.cota, usadas: Math.max(0, (s.cota.usadas || 0) - 1) },
        apagar: { ...estadoInicial.apagar },
        aviso: { tone: 'success', message: 'Foto apagada.' },
      };
    case 'APAGAR_FALHOU':
      return { ...s, apagar: { ...s.apagar, apagando: false, erro: a.mensagem }, apagando: s.apagando.filter((x) => x !== s.apagar.itemId) };
    case 'APAGAR_FECHAR':
      return s.apagar.apagando ? s : { ...s, apagar: { ...estadoInicial.apagar } };

    case 'AVISO_FECHAR':
      return { ...s, aviso: null };
    default:
      return s;
  }
}

/** As fotos escolhidas, na ordem do toque, como a Mídia devolveu (o bloco Galeria copia assetId/origem/w/h/color/alt). */
export function escolhidas(s) {
  const porId = new Map(s.itens.map((i) => [i.assetId, i]));
  return s.selecao.map((id) => porId.get(id)).filter(Boolean);
}
